"use client";

// Light/dark state for the class-based theming in globals.css. The boot script
// in layout.tsx applies the stored (or system) theme before first paint; this
// provider mirrors it after hydration and handles toggling + persistence.

import { createContext, useCallback, useContext, useEffect, useState } from "react";

interface ThemeContextValue {
  dark: boolean;
  toggle: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [dark, setDark] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDark(document.documentElement.classList.contains("dark"));
    }, 0);
    return () => clearTimeout(timer);
  }, []);

  const toggle = useCallback(() => {
    const next = !document.documentElement.classList.contains("dark");
    document.documentElement.classList.toggle("dark", next);
    try {
      localStorage.setItem("jobyab-theme", next ? "dark" : "light");
    } catch {
      // Storage unavailable — the theme still applies for this session.
    }
    setDark(next);
  }, []);

  return <ThemeContext.Provider value={{ dark, toggle }}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used inside ThemeProvider");
  return ctx;
}
